/**
 * WMS API Service Module
 * 
 * Central API client for WMS DAOUXUN system providing:
 * - HTTP request handling with automatic retry logic
 * - JWT token management and automatic refresh
 * - Internationalization header injection
 * - Standardized error handling
 * - Service-specific API wrappers for users, orders, inventory, warehouses
 * 
 * @module services/api
 */
import axios from 'axios';
import {
  API_BASE_URL,
  API_TIMEOUT,
  API_RETRY_ATTEMPTS,
  API_RETRY_DELAY,
  JWT_TOKEN_KEY,
  JWT_REFRESH_KEY,
  DEFAULT_HEADERS,
  ERROR_MESSAGES,
  HTTP_STATUS,
  API_UPLOAD_TIMEOUT,
  SERVICE_URLS,
  API_ENDPOINTS
} from '../config/api.config.js';
import { apiCache, cachedApiCall } from '../utils/apiCache';

/**
 * Main Axios instance configured for WMS API
 * 
 * Configured with:
 * - Base URL from environment
 * - Default timeout and headers
 * - Automatic credentials inclusion
 * - Request/response interceptors for auth and error handling
 * 
 * @constant
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: DEFAULT_HEADERS,
  withCredentials: true
});
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
}



// Offline request queue
const offlineQueue = [];
let isOnline = navigator.onLine;

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    // Process queued requests when back online
    processOfflineQueue();
  });
  
  window.addEventListener('offline', () => {
    isOnline = false;
  });
}

// Process queued offline requests
const processOfflineQueue = async () => {
  while (offlineQueue.length > 0 && isOnline) {
    const { config, resolve, reject } = offlineQueue.shift();
    try {
      const response = await apiClient(config);
      resolve(unwrapStandardResponse(response));
    } catch (error) {
      reject(error);
    }
  }
};

// Request interceptor to add the JWT token and Accept-Language header (English only)
apiClient.interceptors.request.use(
  (config) => {
    // Normalize URL to prevent double /api/ prefix
    // If baseURL ends with /api and request URL starts with /api/, remove the duplicate
    if (config.url && config.url.startsWith('/api/')) {
      const baseURL = config.baseURL || API_BASE_URL;
      if (baseURL && baseURL.endsWith('/api')) {
        // Remove leading /api from the request URL to prevent /api/api/...
        config.url = config.url.substring(4); // Remove '/api' prefix
      }
    }
    
    // Check for token in both possible storage keys (access_token and refresh_token)
    // Some auth flows store the JWT as refresh_token instead of access_token
    const token = localStorage.getItem(JWT_TOKEN_KEY) || localStorage.getItem('wms_refresh_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (typeof window !== 'undefined') {
      const userLocale = localStorage.getItem('userLocale') || 
                        localStorage.getItem('wms-user-locale') || 
                        navigator.language || 
                        'en-US';
      if (userLocale) {
        config.headers['Accept-Language'] = userLocale;
      }
    }
    
    // Ensure Content-Type is set for all POST/PUT/PATCH requests
    // API Gateway requires Content-Type header even for requests without body
    if (['post', 'put', 'patch'].includes((config.method || '').toLowerCase())) {
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

// Response interceptor for managing errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Network error - queue for offline
    if (error.request && error.message === 'Network Error') {
      if (!isOnline && originalRequest._queueOffline !== false) {
        return new Promise((resolve, reject) => {
          offlineQueue.push({
            config: originalRequest,
            resolve,
            reject,
            timestamp: Date.now()
          });
        });
      }
    }

    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem(JWT_REFRESH_KEY);
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;

      try {
        const { API_ENDPOINTS } = await import('../config/api.config.js');
        const response = await axios.post(`${API_ENDPOINTS.AUTH}/refresh`, {
          refresh_token: refreshToken
        });

        const { access_token } = response.data;
        localStorage.setItem(JWT_TOKEN_KEY, access_token);

        isRefreshing = false;
        onRefreshed(access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshed(null);
        localStorage.removeItem(JWT_TOKEN_KEY);
        localStorage.removeItem(JWT_REFRESH_KEY);
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);


/**
 * Retry failed requests with exponential backoff
 * 
 * Automatically retries requests that fail with 5xx server errors.
 * Uses configurable retry attempts and delay between retries.
 * 
 * @param {Function} fn - Async function to execute (API call)
 * @param {number} [retries=API_RETRY_ATTEMPTS] - Number of retry attempts remaining
 * @returns {Promise<any>} Result from successful API call
 * @throws {Error} Original error if all retries exhausted or non-5xx error
 */
const retryRequest = async (fn, retries = API_RETRY_ATTEMPTS) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && error.response?.status >= 500) {
      await new Promise(resolve => setTimeout(resolve, API_RETRY_DELAY));
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

/**
 * Unwrap standardized API responses
 * 
 * Extracts data from standard WMS API response format:
 * { status, data, metadata, message, pagination, links, cache, deprecation }
 * 
 * Also handles deprecation warnings and error responses.
 * 
 * @param {Object} response - Axios response object
 * @returns {Object} Unwrapped response with data and metadata
 * @throws {Error} If response indicates error status
 */
const unwrapStandardResponse = (response) => {
  /**Unwrap standard API response. WMS API client.
  
  Args:
    response: Axios response object
    
  Returns:
    Unwrapped response data
  */
  if (response && response.data) {
    const responseData = response.data;

    if (responseData && typeof responseData === 'object' && 'status' in responseData) {
      if (responseData.status === 'error') {
        const error = new Error(responseData.error?.message || 'API Error');
        error.code = responseData.error?.code;
        error.field = responseData.error?.field;
        error.details = responseData.error?.details;
        error.metadata = responseData.metadata;
        throw error;
      }

      const unwrapped = {
        ...response,
        data: responseData.data !== undefined ? responseData.data : responseData,
        _standardResponse: true,
        _metadata: responseData.metadata,
        _message: responseData.message,
        _pagination: responseData.pagination,
        _links: responseData.links,
        _cache: responseData.cache,
        _deprecation: responseData.deprecation
      };

      if (responseData.deprecation && responseData.deprecation.deprecated) {
        console.warn(
          `[WMS API] Deprecated endpoint: ${responseData.deprecation.alternative_endpoint || 'N/A'} ` +
          `(sunset: ${responseData.deprecation.sunset_date || 'N/A'})`
        );
      }

      return unwrapped;
    }
  }

  return response;
};

/**
 * Main API client with HTTP methods
 * 
 * Provides standardized HTTP methods with automatic:
 * - Retry logic for failed requests
 * - Response unwrapping
 * - Error handling
 * - JWT token injection
 * - i18n header injection
 * 
 * @namespace api
 */
export const api = {
  /**
   * Perform GET request
   * @param {string} url - API endpoint URL
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise<Object>} Unwrapped response data
   */
  get: async (url, config = {}) => {
    const response = await retryRequest(() => apiClient.get(url, config));
    return unwrapStandardResponse(response);
  },

  /**
   * Perform POST request
   * @param {string} url - API endpoint URL
   * @param {Object} [data={}] - Request body data
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise<Object>} Unwrapped response data
   */
  post: async (url, data = {}, config = {}) => {
    const response = await retryRequest(() => apiClient.post(url, data, config));
    return unwrapStandardResponse(response);
  },

  /**
   * Perform PUT request
   * @param {string} url - API endpoint URL
   * @param {Object} [data={}] - Request body data
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise<Object>} Unwrapped response data
   */
  put: async (url, data = {}, config = {}) => {
    const response = await retryRequest(() => apiClient.put(url, data, config));
    return unwrapStandardResponse(response);
  },

  /**
   * Perform PATCH request
   * @param {string} url - API endpoint URL
   * @param {Object} [data={}] - Request body data
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise<Object>} Unwrapped response data
   */
  patch: async (url, data = {}, config = {}) => {
    const response = await retryRequest(() => apiClient.patch(url, data, config));
    return unwrapStandardResponse(response);
  },

  /**
   * Perform DELETE request
   * @param {string} url - API endpoint URL
   * @param {Object} [config={}] - Axios request configuration
   * @returns {Promise<Object>} Unwrapped response data
   */
  delete: async (url, config = {}) => {
    const response = await retryRequest(() => apiClient.delete(url, config));
    return unwrapStandardResponse(response);
  },

  /**
   * Upload file with progress tracking
   * @param {string} url - Upload endpoint URL
   * @param {FormData} formData - Form data containing file
   * @param {Function|null} [onProgress=null] - Progress callback function
   * @returns {Promise<Object>} Unwrapped response data
   */
  cachedGet: async (url, config = {}) => {
    const cacheKey = `${url}${config.params ? JSON.stringify(config.params) : ''}`;
    return cachedApiCall(cacheKey, async () => {
      const response = await retryRequest(() => apiClient.get(url, config));
      return unwrapStandardResponse(response);
    });
  },

  clearCache: () => {
    apiCache.clear();
  },

  invalidateCache: (urlPattern) => {
    apiCache.remove(urlPattern);
  },

  upload: async (url, formData, onProgress = null) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: API_UPLOAD_TIMEOUT,
    };

    if (onProgress) {
      config.onUploadProgress = onProgress;
    }

    const response = await apiClient.post(url, formData, config);
    return unwrapStandardResponse(response);
  }
};

/**
 * Get user preferences from backend
 * 
 * @param {string} userId - User ID to fetch preferences for
 * @returns {Promise<Object>} User preferences object
 */
export async function getUserPreferences(userId) {
  try {
    const { data } = await api.get(`/api/v1/users/${userId}/preferences`, { withCredentials: true });
    return data || {};
  } catch (e) {
    return {};
  }
}

/**
 * Save user preferences to backend
 * 
 * @param {string} userId - User ID to save preferences for
 * @param {Object} prefs - Preferences object to save
 * @returns {Promise<Object>} Updated preferences object
 */
export async function saveUserPreferences(userId, prefs) {
  try {
    const { data } = await api.put(`/api/v1/users/${userId}/preferences`, prefs, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });
    return data || {};
  } catch (e) {
    return {};
  }
}

/**
 * Handle and normalize API errors
 * 
 * Converts Axios errors into standardized error objects with:
 * - Descriptive error messages
 * - Error type classification
 * - Validation error details (if applicable)
 * - Original error for debugging
 * 
 * @param {Error} error - Axios error object
 * @returns {Object} Normalized error object with message, type, and originalError
 */
export const handleApiError = (error) => {
  if (!error.response) {
    // Error network
    return {
      message: ERROR_MESSAGES.NETWORK_ERROR,
      type: 'network',
      originalError: error
    };
  }

  const { status, data } = error.response;

  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return {
        message: data?.message || ERROR_MESSAGES.VALIDATION_ERROR,
        type: 'validation',
        errors: data?.errors || {},
        originalError: error
      };

    case HTTP_STATUS.UNAUTHORIZED:
      return {
        message: ERROR_MESSAGES.UNAUTHORIZED,
        type: 'auth',
        originalError: error
      };

    case HTTP_STATUS.FORBIDDEN:
      return {
        message: ERROR_MESSAGES.FORBIDDEN,
        type: 'permission',
        originalError: error
      };

    case HTTP_STATUS.NOT_FOUND:
      return {
        message: ERROR_MESSAGES.NOT_FOUND,
        type: 'notFound',
        originalError: error
      };

    case HTTP_STATUS.VALIDATION_ERROR:
      return {
        message: data?.message || ERROR_MESSAGES.VALIDATION_ERROR,
        type: 'validation',
        errors: data?.errors || {},
        originalError: error
      };

    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
    case HTTP_STATUS.BAD_GATEWAY:
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
      return {
        message: ERROR_MESSAGES.SERVER_ERROR,
        type: 'server',
        originalError: error
      };

    default:
      return {
        message: data?.message || ERROR_MESSAGES.SERVER_ERROR,
        type: 'unknown',
        originalError: error
      };
  }
};

/**
 * JWT token management utilities
 * 
 * @namespace tokenUtils
 */
export const tokenUtils = {
  /**
   * Get access token from localStorage
   * @returns {string|null} JWT access token
   */
  getToken: () => localStorage.getItem(JWT_TOKEN_KEY),
  
  /**
   * Get refresh token from localStorage
   * @returns {string|null} JWT refresh token
   */
  getRefreshToken: () => localStorage.getItem(JWT_REFRESH_KEY),
  
  /**
   * Store access and refresh tokens in localStorage
   * @param {string} accessToken - JWT access token
   * @param {string} [refreshToken] - JWT refresh token (optional)
   */
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(JWT_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(JWT_REFRESH_KEY, refreshToken);
    }
  },
  
  /**
   * Clear all tokens from localStorage
   */
  clearTokens: () => {
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(JWT_REFRESH_KEY);
  },
  
  /**
   * Check if JWT token is expired
   * @param {string} token - JWT token to check
   * @returns {boolean} True if token is expired or invalid
   */
  isTokenExpired: (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
};

/**
 * User management service
 * 
 * Provides CRUD operations and user management functions
 * 
 * @namespace userService
 */

/**
 * Customer management service
 * 
 * Provides CRUD operations for B2B customers
 * 
 * @namespace customerService
 */
export const customerService = {
  getCustomers: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMERS, { params });
      const data = response?.data ?? {};
      // Handle both direct array response and { customers: [...] } format
      let items = [];
      if (Array.isArray(data)) {
        items = data;
      } else if (Array.isArray(data?.customers)) {
        items = data.customers;
      } else if (Array.isArray(data?.items)) {
        items = data.items;
      }
      const total = data?.pagination?.total || data?.total || items.length;
      return { items, total, raw: data };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getCustomer: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.CUSTOMERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  createCustomer: async (payload) => {
    try {
      const response = await api.post(API_ENDPOINTS.CUSTOMERS, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateCustomer: async (id, payload) => {
    try {
      const response = await api.put(`${API_ENDPOINTS.CUSTOMERS}/${id}`, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteCustomer: async (id) => {
    try {
      const response = await api.delete(`${API_ENDPOINTS.CUSTOMERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getAnalytics: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.CUSTOMERS}/${id}/analytics`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};
/**
 * Product management service
 * 
 * Provides CRUD operations for product master data
 * 
 * @namespace productService
 */
export const productService = {
  getProducts: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.PRODUCTS, { params });
      const data = response?.data ?? {};
      // API returns array directly or wrapped in items/data/products
      const items = Array.isArray(data) ? data :
                   Array.isArray(data?.items) ? data.items : 
                   Array.isArray(data?.data) ? data.data :
                   Array.isArray(data?.products) ? data.products : [];
      const total = data?.pagination?.total || data?.meta?.total || data?.total || items.length;
      return { items, total, products: items, raw: data };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getProduct: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  createProduct: async (payload) => {
    try {
      const response = await api.post(API_ENDPOINTS.PRODUCTS, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateProduct: async (id, payload) => {
    try {
      const response = await api.put(`${API_ENDPOINTS.PRODUCTS}/${id}`, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`${API_ENDPOINTS.PRODUCTS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getCategories: async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/categories`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getStats: async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ANALYTICS}/products/stats`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};


export const userService = {
  /**
   * Get list of users with pagination
   * @param {Object} [params={}] - Query parameters (page, limit, filters)
   * @returns {Promise<{items: Array, total: number, raw: Object}>} Users list with metadata
   */
  getUsers: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.USERS, { params });
      const data = response?.data ?? {};
      const items = Array.isArray(data?.users)
        ? data.users
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
      const total = typeof data?.total === 'number' ? data.total : (typeof data?.meta?.total === 'number' ? data.meta.total : items.length);
      return { items, total, raw: data };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get single user by ID
   * @param {string} id - User ID
   * @returns {Promise<Object>} User object
   */
  getUser: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.USERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create new user
   * @param {Object} payload - User data
   * @returns {Promise<Object>} Created user object
   */
  createUser: async (payload) => {
    try {
      const response = await api.post(API_ENDPOINTS.USERS, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update existing user
   * @param {string} id - User ID
   * @param {Object} payload - Updated user data
   * @returns {Promise<Object>} Updated user object
   */
  updateUser: async (id, payload) => {
    try {
      const response = await api.put(`${API_ENDPOINTS.USERS}/${id}`, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete user
   * @param {string} id - User ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteUser: async (id) => {
    try {
      const response = await api.delete(`${API_ENDPOINTS.USERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Reset user password
   * @param {string} id - User ID
   * @returns {Promise<Object>} Password reset confirmation
   */
  resetPassword: async (id) => {
    try {
      const response = await api.post(`${API_ENDPOINTS.USERS}/${id}/reset-password`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Assign roles/permissions to user
   * @param {string} id - User ID
   * @param {Array<string>} permissions - Array of permission names
   * @returns {Promise<Object>} Updated user with roles
   */
  assignRoles: async (id, permissions) => {
    try {
      const response = await api.post(`/api/v1/auth/users/${id}/roles`, { permissions });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

/**
 * Order management service
 * 
 * Handles order operations including stats, listings, and marketplace analytics
 * 
 * @namespace orderService
 */
export const orderService = {
  getOrders: async (params = {}) => {
    try {
      // Defensive sanitization for page/limit to prevent [object Object] 422 errors
      const sanitizedParams = { ...params };
      if (typeof sanitizedParams.page === 'object') sanitizedParams.page = 1;
      if (typeof sanitizedParams.limit === 'object') sanitizedParams.limit = 20;

      const response = await api.get(API_ENDPOINTS.ORDERS, { params: sanitizedParams });
      const data = response?.data ?? {};
      const rawItems = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.orders) ? data.orders : (Array.isArray(data) ? data : []));
      
      // Transform API response to frontend expected format
      const items = rawItems.map(order => ({
        ...order,
        // Map API fields to frontend expected fields
        orderNumber: order.reference || order.order_number || `ORD-${order.id}`,
        customerName: order.customer_name || order.customer?.name || '-',
        customerEmail: order.customer_email || order.customer?.email || '-',
        marketplace: order.marketplace || order.channel || '-',
        items: order.items || order.line_items || [],
        lineItemsCount: order.line_items_count || order.items?.length || 0,
        fulfillmentTarget: order.fulfillment_target || null,
        shippingAddress: order.shipping_address || order.shippingAddress || null,
        notes: order.notes || '',
        priority: order.priority || 'NORMAL'
      }));
      
      const total = data?.total || data?.total_count || data?.pagination?.total_items || items.length;
      return { items, total, raw: data };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getOrderById: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  createOrder: async (payload) => {
    try {
      const response = await api.post(API_ENDPOINTS.ORDERS, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateOrder: async (id, payload) => {
    try {
      const response = await api.put(`${API_ENDPOINTS.ORDERS}/${id}`, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteOrder: async (id) => {
    try {
      const response = await api.delete(`${API_ENDPOINTS.ORDERS}/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getOrderStats: async (params = {}) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/stats`, { params });
      const data = response?.data ?? {};
      // API returns { statistics: { total_orders, ... } } - extract and flatten for frontend
      const stats = data?.statistics || data;
      return {
        total_orders: stats?.total_orders || 0,
        total: stats?.total_orders || 0,
        pending_orders: stats?.status_breakdown?.pending || 0,
        pending: stats?.status_breakdown?.pending || 0,
        completed_orders: stats?.status_breakdown?.completed || 0,
        completed: stats?.status_breakdown?.completed || 0,
        in_transit: stats?.status_breakdown?.shipped || stats?.status_breakdown?.in_transit || 0,
        shipping: stats?.status_breakdown?.shipped || 0,
        total_revenue: stats?.total_revenue || 0,
        average_order_value: stats?.average_order_value || 0,
        status_breakdown: stats?.status_breakdown || {},
        warehouse_breakdown: stats?.warehouse_breakdown || [],
        trend: stats?.trend || null,
        // Manager Dashboard metrics
        orders_per_hour: stats?.orders_per_hour || 0,
        fulfillment_rate: stats?.fulfillment_rate || 0,
        estimated_completion: stats?.estimated_completion || '-',
        raw: data
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getStats: async (params = {}) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/stats`, { params });
      const data = response?.data ?? {};
      // API returns { statistics: { total_orders, ... } } - extract and flatten for frontend
      const stats = data?.statistics || data;
      return {
        total_orders: stats?.total_orders || 0,
        total: stats?.total_orders || 0,
        pending_orders: stats?.status_breakdown?.pending || 0,
        pending: stats?.status_breakdown?.pending || 0,
        completed_orders: stats?.status_breakdown?.completed || 0,
        completed: stats?.status_breakdown?.completed || 0,
        in_transit: stats?.status_breakdown?.shipped || stats?.status_breakdown?.in_transit || 0,
        shipping: stats?.status_breakdown?.shipped || 0,
        total_revenue: stats?.total_revenue || 0,
        average_order_value: stats?.average_order_value || 0,
        status_breakdown: stats?.status_breakdown || {},
        warehouse_breakdown: stats?.warehouse_breakdown || [],
        trend: stats?.trend || null,
        // Manager Dashboard metrics
        orders_per_hour: stats?.orders_per_hour || 0,
        fulfillment_rate: stats?.fulfillment_rate || 0,
        estimated_completion: stats?.estimated_completion || '-',
        raw: data
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getUrgentOrders: async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/urgent`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getMarketplacePerformance: async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/marketplace-performance`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};
export const inventoryService = {
  /**
   * Get inventory items with optional filtering
   * Used by 3D warehouse visualization to display pallets on shelves
   * @param {Object} filters - Query parameters for filtering inventory
   * @param {string} [filters.warehouse_id] - Filter by warehouse
   * @param {string} [filters.location] - Filter by storage location
   * @param {number} [filters.page] - Page number for pagination
   * @param {number} [filters.limit] - Items per page
   * @returns {Promise<{items: Array, total: number}>} Inventory items with total count
   */
  getInventory: async (filters = {}) => {
    try {
      // Use /api/v1/inventory-items endpoint which contains actual inventory data
      // The /api/v1/inventory endpoint returns empty array
      const res = await api.get('/api/v1/inventory-items', { params: filters });
      const data = res?.data;
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
      const total = (typeof data?.total === 'number') ? data.total : list.length;
      return {
        items: list,
        total
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get inventory statistics formatted for dashboard display
   * @returns {Promise<Array|Object>} Array of stat cards or raw stats object
   */
  getStats: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.INVENTORY_STATS}`);
      const data = res?.data;
      if (data?.stats) {
        return [
          { label: 'Total Products', value: data.stats.total_products || 0, trend: '+0%', color: 'primary' },
          { label: 'Total Quantity', value: data.stats.total_quantity || 0, trend: '+0%', color: 'success' },
          { label: 'Low Stock Alerts', value: data.stats.low_stock_alerts || 0, trend: '+0%', color: 'warning' },
          { label: 'Movements', value: data.stats.total_movements || 0, trend: '+0%', color: 'info' },
          { label: 'Active Reservations', value: data.stats.active_reservations || 0, trend: '+0%', color: 'secondary' },
          { label: 'Locations', value: data.stats.locations_count || 0, trend: '+0%', color: 'primary' }
        ];
      }
      return data || {};
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get paginated inventory items
   * @param {number} [page=1] - Page number (1-indexed)
   * @param {number} [limit=20] - Number of items per page
   * @returns {Promise<{items: Array, total: number}>} Inventory items with total count
   */
  getItems: async (page = 1, limit = 20) => {
    try {
      const res = await api.get(`${API_ENDPOINTS.INVENTORY}/items`, { params: { page, limit } });
      const data = res?.data;
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
      const total = (typeof data?.total === 'number') ? data.total : list.length;
      return {
        items: list,
        total
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get inventory alerts (low stock, expiring, etc.)
   * @returns {Promise<Array>} Array of inventory alerts
   */
  getAlerts: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.INVENTORY}/alerts`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get cycle count status and statistics
   * @returns {Promise<Object>} Cycle count status
   */
  getCycleCountStatus: async () => {
    try {
      const res = await api.get('/api/v1/inventory-adjustments/cycle-count');
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Initiate a new cycle count
   * @param {Object} data - Cycle count data (warehouse_id, location_ids, product_categories)
   * @returns {Promise<Object>} Created cycle count
   */
  createCycleCount: async (data) => {
    try {
      const res = await api.post('/api/v1/inventory-adjustments/cycle-count', data);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Submit a physical count entry for a cycle count
   * @param {Object} data - Count entry data (location_id, product_id, counted_quantity, counter_id, notes)
   * @returns {Promise<Object>} Recorded count entry
   */
  submitCycleCountEntry: async (data) => {
    try {
      const res = await api.post('/api/v1/inventory-adjustments/physical-count', data);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get physical count status
   * @returns {Promise<Object>} Physical count status
   */
  getPhysicalCountStatus: async () => {
    try {
      const res = await api.get('/api/v1/inventory-adjustments/physical-count');
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

/**
 * Warehouse management service
 * 
 * Handles warehouse operations, stats, billing, and routing rules
 * 
 * @namespace warehouseService
 */
export const warehouseService = {
  /**
   * Get warehouse statistics summary
   * @returns {Promise<Object>} Warehouse statistics
   */
  getStats: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.INVENTORY}/summary`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get list of all warehouses
   * @returns {Promise<Array>} Array of warehouse objects
   */
  getWarehouses: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.DASHBOARD.replace('/dashboard','')}/stats/warehouse`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get single warehouse details
   * @param {string} warehouseId - Warehouse ID
   * @returns {Promise<Object>} Warehouse details
   */
  getWarehouse: async (warehouseId) => {
    try {
      const res = await api.get(`${API_ENDPOINTS.DASHBOARD.replace('/dashboard','')}/stats/warehouse/${warehouseId}`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get warehouse billing information
   * @returns {Promise<Object>} Billing data
   */
  getBilling: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.DASHBOARD.replace('/dashboard','')}/stats/warehouse`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get warehouse routing rules
   * @returns {Promise<Array>} Array of routing rules
   */
  getRoutingRules: async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.DASHBOARD.replace('/dashboard','')}/stats/warehouse`);
      return res?.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

/**
 * Analytics service
 * 
 * Provides analytics data, KPIs, metrics, and reporting
 * 
 * @namespace analyticsService
 */
export const analyticsService = {
  /** @returns {Promise<Object>} Key performance indicators */
  getKPIs: () => api.get(`${API_ENDPOINTS.ANALYTICS}/kpis`),
  
  /** @returns {Promise<Object>} System metrics */
  getMetrics: () => api.get(`${API_ENDPOINTS.ANALYTICS}/metrics`),
  
  /** @returns {Promise<Array>} Available reports */
  getReports: () => api.get(`${API_ENDPOINTS.ANALYTICS}/reports`),
  
  /** @returns {Promise<Array>} Analytics alerts */
  getAlerts: () => api.get(`${API_ENDPOINTS.ANALYTICS}/alerts`),
  
  /** @returns {Promise<Object>} Dashboard analytics data */
  getDashboardData: () => api.get(`${API_ENDPOINTS.ANALYTICS}/dashboard`),
  
  /** @returns {Promise<Array>} AR visualization elements */
  getARElements: () => api.get(`${API_ENDPOINTS.ANALYTICS}/ar-elements`),
};

/**
 * Maintenance service
 * 
 * Handles equipment maintenance, tasks, and alerts
 * 
 * @namespace maintenanceService
 */
export const maintenanceService = {
  /** @returns {Promise<Array>} Simplified equipment list */
  getEquipmentSimple: () => api.get('/api/v1/maintenance/equipment-simple'),
  
  /** @returns {Promise<Object>} Maintenance statistics */
  getMaintenanceStats: () => api.get('/api/v1/maintenance/stats'),
  
  /** @returns {Promise<Object>} Maintenance system health */
  getMaintenanceHealth: () => api.get('/api/v1/maintenance/health'),
  
  /** @returns {Promise<Object>} Demo data creation result */
  createDemoData: () => api.post('/api/v1/maintenance/demo-data'),
  
  /**
   * Get equipment details
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<Object>} Equipment details
   */
  getEquipment: (equipmentId) => api.get(`/v1/maintenance/equipment/${equipmentId}`),
  
  /**
   * Create new equipment
   * @param {Object} equipment - Equipment data
   * @returns {Promise<Object>} Created equipment
   */
  createEquipment: (equipment) => api.post('/api/v1/maintenance/equipment', equipment),
  
  /**
   * Update equipment
   * @param {string} equipmentId - Equipment ID
   * @param {Object} equipment - Updated equipment data
   * @returns {Promise<Object>} Updated equipment
   */
  updateEquipment: (equipmentId, equipment) => api.put(`/v1/maintenance/equipment/${equipmentId}`, equipment),
  
  /**
   * Get tasks for equipment
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<Array>} Equipment tasks
   */
  getEquipmentTasks: (equipmentId) => api.get(`/v1/maintenance/equipment/${equipmentId}/tasks`),
  
  /**
   * Create maintenance task
   * @param {Object} task - Task data
   * @returns {Promise<Object>} Created task
   */
  createTask: (task) => api.post('/api/v1/maintenance/tasks', task),
  
  /**
   * Update maintenance task
   * @param {string} taskId - Task ID
   * @param {Object} task - Updated task data
   * @returns {Promise<Object>} Updated task
   */
  updateTask: (taskId, task) => api.put(`/v1/maintenance/tasks/${taskId}`, task),
  
  /**
   * Start maintenance task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Started task
   */
  startTask: (taskId) => api.post(`/v1/maintenance/tasks/${taskId}/start`),
  
  /**
   * Complete maintenance task
   * @param {string} taskId - Task ID
   * @param {string} workPerformed - Work description
   * @param {number} actualCost - Actual cost incurred
   * @returns {Promise<Object>} Completed task
   */
  completeTask: (taskId, workPerformed, actualCost) => api.post(`/v1/maintenance/tasks/${taskId}/complete`, {
    work_performed: workPerformed,
    actual_cost: actualCost
  }),
  
  /**
   * Get maintenance alerts
   * @param {boolean} acknowledged - Filter by acknowledgement status
   * @returns {Promise<Array>} Maintenance alerts
   */
  getAlerts: (acknowledged) => api.get('/api/v1/maintenance/alerts', { params: { acknowledged } }),
  
  /**
   * Acknowledge maintenance alert
   * @param {string} alertId - Alert ID
   * @param {string} acknowledgedBy - User acknowledging the alert
   * @returns {Promise<Object>} Acknowledged alert
   */
  acknowledgeAlert: (alertId, acknowledgedBy) => api.post(`/v1/maintenance/alerts/${alertId}/acknowledge`, {
    acknowledged_by: acknowledgedBy
  }),
  
  /** @returns {Promise<Object>} Generated alerts result */
  generateAlerts: () => api.post('/api/v1/maintenance/alerts/generate'),
  
  /** @returns {Promise<Object>} Maintenance dashboard data */
  getDashboard: () => api.get('/api/v1/maintenance/dashboard')
};

export const userManagementService = {
  getUsersSimple: () => api.get('/api/v1/user-management/users-simple'),
  getUserStats: () => api.get('/api/v1/user-management/stats'),
  getUserHealth: () => api.get('/api/v1/user-management/health'),
  createDemoData: () => api.post('/api/v1/user-management/demo-data'),
  getUser: (userId) => api.get(`/v1/user-management/users/${userId}`),
  createUser: (user) => api.post('/api/v1/user-management/users', user),
  updateUser: (userId, user) => api.put(`/v1/user-management/users/${userId}`, user),
  getUserPreferences: (userId) => api.get(`/v1/user-management/users/${userId}/preferences`),
  updateUserPreferences: (userId, preferences) => api.put(`/v1/user-management/users/${userId}/preferences`, preferences),
  getUserSessions: (userId) => api.get(`/v1/user-management/users/${userId}/sessions`),
  getUserActivities: (userId) => api.get(`/v1/user-management/users/${userId}/activities`),
  terminateUserSessions: (userId) => api.post(`/v1/user-management/users/${userId}/terminate-sessions`),
  updateUserStatus: (userId, status) => api.put(`/v1/user-management/users/${userId}/status`, { status })
};

export const receivingService = {
  getReceivings: () => api.get(`${API_ENDPOINTS.RECEIVING}/all`),
  getReceiving: (receivingId) => api.get(`${API_ENDPOINTS.RECEIVING}/${receivingId}`),
  startReceiving: (receivingId, deliveryNote) => api.post(`${API_ENDPOINTS.RECEIVING}/${receivingId}/start`, { delivery_note: deliveryNote }),
  receiveLine: (receivingId, lineData) => api.post(`${API_ENDPOINTS.RECEIVING}/${receivingId}/receive-line`, lineData),
  completeReceiving: (receivingId, notes) => api.post(`${API_ENDPOINTS.RECEIVING}/${receivingId}/complete`, { notes })
};

export const qualityService = {
  getQualityControls: () => api.get(`${API_ENDPOINTS.QUALITY}/controls-simple`),
  getQualityStats: () => api.get(`${API_ENDPOINTS.QUALITY}/stats`),
  updateQualityControl: (controlId, data) => api.put(`${API_ENDPOINTS.QUALITY}/controls/${controlId}`, data),
  getControlDefects: (controlId) => api.get(`${API_ENDPOINTS.QUALITY}/controls/${controlId}/defects`),
  createDefect: (controlId, defectData) => api.post(`${API_ENDPOINTS.QUALITY}/controls/${controlId}/defects`, defectData),
  getControlActions: (controlId) => api.get(`${API_ENDPOINTS.QUALITY}/controls/${controlId}/actions`),
  createAction: (controlId, actionData) => api.post(`${API_ENDPOINTS.QUALITY}/controls/${controlId}/actions`, actionData)
};

/**
 * System Settings Service - Inspired by Arcane admin management
 * 
 * Provides CRUD operations for system-wide configuration settings
 * with category filtering, audit trails, and admin-only operations.
 * 
 * @namespace systemSettingsService
 */
export const systemSettingsService = {
  /**
   * Get all system settings
   * Admin users see all settings, other users see only public settings
   * @returns {Promise<Object>} Settings list with total count
   */
  getAllSettings: async () => {
    try {
      const response = await api.get('/api/v1/admin/settings/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get all setting categories with counts
   * @returns {Promise<Object>} Categories list
   */
  getCategories: async () => {
    try {
      const response = await api.get('/api/v1/admin/settings/categories');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get settings by category
   * @param {string} category - Category name
   * @returns {Promise<Object>} Settings in category
   */
  getSettingsByCategory: async (category) => {
    try {
      const response = await api.get(`/api/v1/admin/settings/category/${category}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get a single setting by key
   * @param {string} key - Setting key
   * @returns {Promise<Object>} Setting details
   */
  getSetting: async (key) => {
    try {
      const response = await api.get(`/api/v1/admin/settings/${key}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get change history for a setting (admin only)
   * @param {string} key - Setting key
   * @param {number} limit - Max history items
   * @returns {Promise<Object>} History list
   */
  getSettingHistory: async (key, limit = 50) => {
    try {
      const response = await api.get(`/api/v1/admin/settings/${key}/history`, { params: { limit } });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update a single setting (admin only)
   * @param {string} key - Setting key
   * @param {any} value - New value
   * @param {string} changeReason - Reason for change
   * @returns {Promise<Object>} Updated setting
   */
  updateSetting: async (key, value, changeReason = null) => {
    try {
      const response = await api.put(`/api/v1/admin/settings/${key}`, {
        value,
        change_reason: changeReason
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update multiple settings at once (admin only)
   * @param {Object} settings - Key-value pairs
   * @param {string} changeReason - Reason for changes
   * @returns {Promise<Object>} Update results
   */
  updateSettingsBatch: async (settings, changeReason = null) => {
    try {
      const response = await api.put('/api/v1/admin/settings/', {
        settings,
        change_reason: changeReason
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a new setting (admin only)
   * @param {Object} settingData - Setting data
   * @returns {Promise<Object>} Created setting
   */
  createSetting: async (settingData) => {
    try {
      const response = await api.post('/api/v1/admin/settings/', settingData);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Reset a setting to its default value (admin only)
   * @param {string} key - Setting key
   * @returns {Promise<Object>} Reset setting
   */
  resetSetting: async (key) => {
    try {
      const response = await api.post(`/api/v1/admin/settings/${key}/reset`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a setting (admin only)
   * @param {string} key - Setting key
   * @returns {Promise<Object>} Deletion result
   */
  deleteSetting: async (key) => {
    try {
      const response = await api.delete(`/api/v1/admin/settings/${key}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export { apiClient, SERVICE_URLS };

export default api;
